import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { apiErrorMessage } from "../lib/errors";
import { fetchMe, type MeResponse } from "../lib/auth";
import { LandingHeader, LandingFooter } from "../components/landing-chrome";

interface Tier {
  id: "starter" | "pro" | "studio";
  name: string;
  price: string;
  blurb: string;
  features: string[];
  inheritsFrom?: string;
  highlighted?: boolean;
}

const TIERS: Tier[] = [
  {
    id: "starter",
    name: "Starter",
    price: "$12",
    blurb: "For solo creators getting started",
    features: [
      "30 generations / month",
      "Standard AI models (gpt-4o-mini, gpt-5-mini)",
      "5-format publishing (X / Bluesky / LinkedIn / Threads / Instagram)",
      "Voice training + scheduling",
      "Email support",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "$29",
    blurb: "For serious creators who ship daily",
    inheritsFrom: "Starter",
    features: [
      "100 generations / month (3.3× Starter)",
      "Flagship + reasoning models (gpt-5, gpt-4o, o3-mini)",
      "Auto-plug — voice-matched follow-ups on every post",
      "Auto-RT + thread scheduling (coming soon)",
      "Priority email support",
    ],
    highlighted: true,
  },
  {
    id: "studio",
    name: "Studio",
    price: "$79",
    blurb: "For agencies + power users",
    inheritsFrom: "Pro",
    features: [
      "400 generations / month (4× Pro)",
      "o3 reasoning model (Studio exclusive)",
      "Beta features early access",
      "Same-day priority support",
      "Direct line to founders (Slack Connect)",
    ],
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchMe().then(setMe).catch(console.error);
  }, []);

  async function handleSelect(plan: Tier["id"]) {
    if (!me) {
      navigate("/login?next=pricing");
      return;
    }
    setLoading(plan);
    try {
      const { url } = await api.post<{ url: string }>("/api/billing/checkout", {
        plan,
      });
      window.location.href = url;
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <LandingHeader />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 -top-32 h-[420px] bg-brand-gradient-soft blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 sm:px-6 sm:pt-20">
          <div className="text-center">
            <span className="badge-brand">Pricing</span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-ink-900 sm:text-5xl">
              Simple. Transparent.{" "}
              <span className="text-gradient">7-day free trial.</span>
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-ink-600">
              Try any paid plan free for 7 days. Cancel anytime in those 7 days
              and you won't be charged a cent.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-24 pt-12 sm:px-6 sm:pt-16">
        <div className="grid gap-5 md:grid-cols-3">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                tier.highlighted
                  ? "border-brand-300 shadow-md ring-1 ring-brand-200"
                  : "border-ink-200/70 shadow-xs"
              }`}
            >
              {tier.highlighted ? (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-gradient px-3 py-1 text-2xs font-semibold uppercase tracking-wider text-white shadow-sm">
                  Most popular
                </span>
              ) : null}
              <h3 className="text-lg font-semibold text-ink-900">{tier.name}</h3>
              <p className="mt-1 text-sm text-ink-500">{tier.blurb}</p>
              <p className="mt-5 flex items-baseline gap-1 text-ink-900">
                <span className="text-4xl font-bold tracking-tight">
                  {tier.price}
                </span>
                <span className="text-sm font-normal text-ink-500">/mo</span>
              </p>
              {tier.inheritsFrom ? (
                <p className="mt-5 text-2xs font-semibold uppercase tracking-wider text-ink-500">
                  Everything in {tier.inheritsFrom}, plus
                </p>
              ) : null}
              <ul
                className={`${tier.inheritsFrom ? "mt-2" : "mt-5"} flex-1 space-y-2.5 text-sm text-ink-700`}
              >
                {tier.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-500" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                className={`mt-6 ${tier.highlighted ? "btn-brand" : "btn-secondary"}`}
                disabled={loading === tier.id}
                onClick={() => handleSelect(tier.id)}
              >
                {loading === tier.id ? "Loading..." : "Start 7-day free trial"}
              </button>
              <p className="mt-2 text-center text-2xs text-ink-500">
                No charge for 7 days · cancel anytime
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <h2 className="mb-6 text-center text-2xl font-bold tracking-tight text-ink-900">
            Billing FAQ
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            <FaqCard
              q="What's a generation?"
              a="One Generate run produces 5 native variants — X, Bluesky, LinkedIn, Newsletter, Short-video — all in one credit."
            />
            <FaqCard
              q="What happens after the 7 days?"
              a="On day 8 you're billed the regular monthly price for the plan you picked. You'll get an email receipt and your monthly credits reset on the same day each month."
            />
            <FaqCard
              q="Can I bring my own AI key?"
              a="Yes. Add your OpenAI / Anthropic key in Settings → AI keys. When BYOK is on, AI usage is free — you only spend platform credits."
            />
            <FaqCard
              q="How does the 7-day free trial work?"
              a="Pick a plan, enter a card, and use everything free for 7 days. Cancel any time in those 7 days and your card is never charged. Day 8 the first invoice is billed automatically — same price you saw at signup."
            />
            <FaqCard
              q="Can I cancel anytime?"
              a="Yes. Cancel in Settings → Billing or via the Stripe portal. During the trial cancellation is instant and free. After the trial, your plan stays active until the end of the current period — never auto-charged again."
            />
            <FaqCard
              q="What payment methods do you accept?"
              a="All Stripe-supported methods — Visa, Mastercard, Amex, Discover, Apple Pay, Google Pay, Link, and select local methods (SEPA, iDEAL, Bancontact, etc.)."
            />
            <FaqCard
              q="Can I switch plans mid-month?"
              a="Yes. Upgrades take effect immediately with prorated charge. Downgrades take effect at the end of the current billing cycle so you don't lose paid credits."
            />
            <FaqCard
              q="Is my data used to train AI?"
              a="No. Your posts, voice profiles, and analytics live in your private Cloudflare D1 database. We don't sell, share, or fine-tune on your data — ever."
            />
          </div>
        </div>

        <div className="mt-12 rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-6 text-center">
          <p className="text-sm font-medium text-emerald-900">
            7-day free trial · Cancel anytime · No surprise charges
          </p>
          <p className="mt-1 text-xs text-emerald-800/80">
            We collect a card up front so the plan continues seamlessly after
            day 7. Cancel anytime during the trial in Settings → Billing — no
            charge.
          </p>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}

function FaqCard({ q, a }: { q: string; a: string }) {
  return (
    <div className="rounded-xl border border-ink-200/70 bg-white p-5 shadow-xs">
      <p className="text-sm font-semibold text-ink-900">{q}</p>
      <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{a}</p>
    </div>
  );
}
